import _assign from "object-assign";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { forwardRef, useState, Fragment, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FormControl, FormHelperText } from '@material-ui/core';
import { useForkRef } from '@material-ui/core/utils';
import { FormLabel, Necessity } from '@jpmuitk/form-label';
import { usePopout } from '@jpmuitk/popper';
import { useDensity, withStyles } from '@jpmuitk/theme';
import { useId, a11yContext } from '@jpmuitk/utils';
import { Tooltip } from '@jpmuitk/tooltip';
import { useAriaAnnouncer } from '@jpmuitk/aria-announcer';
import ActivationIndicator from './internal/ActivationIndicator';
import FormFieldContext from './internal/FormFieldContext';
import FormFieldPopoutContext from './internal/FormFieldPopoutContext';
import StatusIndicatorMessage from './internal/StatusIndicatorMessage';
import useIsHoveredOrFocused from './internal/useIsHoveredOrFocused';
import useFormFieldProps from './useFormFieldProps';
import { style } from './style';

var callAll = function callAll() {
  for (var _len = arguments.length, handlers = new Array(_len), _key = 0; _key < _len; _key++) {
    handlers[_key] = arguments[_key];
  }

  return function (eventArg) {
    return handlers.forEach(function (handler) {
      return handler && handler(eventArg);
    });
  };
};

var noop = function noop() {
  return undefined;
};

var combineMethodsAndProps = function combineMethodsAndProps(methods, props) {
  return Object.entries(methods).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        prop = _ref2[0],
        fn = _ref2[1];

    return [prop, callAll(fn, props[prop])];
  }).reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        prop = _ref4[0],
        fn = _ref4[1];

    acc[prop] = fn;
    return acc;
  }, {});
};

var FormFieldLabel = function FormFieldLabel(_ref5) {
  var disabled = _ref5.disabled,
      hasStatusIndicator = _ref5.hasStatusIndicator,
      StatusIndicatorProps = _ref5.StatusIndicatorProps,
      label = _ref5.label,
      LabelComponent = _ref5.LabelComponent,
      classes = _ref5.classes,
      density = _ref5.density,
      labelId = _ref5.labelId,
      labelPlacement = _ref5.labelPlacement,
      required = _ref5.required,
      LabelProps = _ref5.LabelProps;
  return label ? /*#__PURE__*/React.createElement(LabelComponent, _assign({
    StatusIndicatorProps: StatusIndicatorProps,
    classes: {
      root: classes.labelRoot
    },
    density: density,
    disabled: disabled,
    hasStatusIndicator: hasStatusIndicator,
    id: labelId,
    labelPlacement: labelPlacement,
    required: required
  }, LabelProps), label) : null;
}; // eslint-disable-next-line complexity


var ControlContainer = /*#__PURE__*/forwardRef(function ControlContainer(props, ref) {
  var _clsx, _clsx2, _clsx3;

  // FIXME: Hover over a FormField and causes the component to rerender. Is this intended?
  // useTraceUpdate(props, 'ControlContainer');
  var LabelComponent = props.LabelComponent,
      HelperTextComponent = props.HelperTextComponent,
      classes = props.classes,
      density = props.density,
      disabled = props.disabled,
      fullWidth = props.fullWidth,
      hasStatusIndicator = props.hasStatusIndicator,
      StatusIndicatorProps = props.StatusIndicatorProps,
      helperText = props.helperText,
      helperTextId = props.helperTextId,
      HelperTextProps = props.HelperTextProps,
      required = props.required,
      label = props.label,
      labelId = props.labelId,
      LabelProps = props.LabelProps,
      labelPlacement = props.labelPlacement,
      lowEmphasisFocus = props.lowEmphasisFocus,
      popoutMode = props.popoutMode,
      popoutStyleProp = props.popoutStyle,
      children = props.children,
      validationState = props.validationState,
      variant = props.variant,
      rest = _objectWithoutProperties(props, ["LabelComponent", "HelperTextComponent", "classes", "density", "disabled", "fullWidth", "hasStatusIndicator", "StatusIndicatorProps", "helperText", "helperTextId", "HelperTextProps", "required", "label", "labelId", "LabelProps", "labelPlacement", "lowEmphasisFocus", "popoutMode", "popoutStyle", "children", "validationState", "variant"]);

  var _useFormFieldProps = useFormFieldProps(),
      focusVisible = _useFormFieldProps.focusVisible,
      focused = _useFormFieldProps.focused;

  var contentRef = useRef(null);
  var placeholderRef = useRef(null);
  var controlAreaRef = useRef(null); // TODO: Think about how to avoid invoking usePopout code when not in popout mode.
  // Maybe create a 'PopoutControlContainer'? Tidy up DOM structure in that?

  var _usePopout = usePopout({
    childRef: contentRef,
    fullWidth: fullWidth
  }),
      setPlaceholderRef = _usePopout.setPlaceholderRef,
      placeholderStyle = _usePopout.placeholderStyle,
      setPopperRef = _usePopout.setPopperRef,
      popperStyle = _usePopout.popperStyle,
      actions = _usePopout.actions,
      popperState = _usePopout.popperState,
      popperTallerThanPlaceholder = _usePopout.popperTallerThanPlaceholder;

  var forkedPlaceholderRef = useForkRef(placeholderRef, setPlaceholderRef); // Indicates whether popout state is managed by child component. This should be rarely used
  // unless the child component has special focused state that is not focusing on the main
  // form control. E.g. TokenizedInput's clear button can be focused separately.

  var _useState = useState(false),
      managedPopout = _useState[0],
      setManagedPopout = _useState[1];

  var _useState2 = useState(focused),
      isPoppedOut = _useState2[0],
      setIsPoppedOut = _useState2[1];

  var isLabelLeft = labelPlacement === 'left';
  var applyFocusClass = focusVisible && focused;
  var popoutModeLabelTop = popoutMode && !isLabelLeft;
  var popoutModeLabelLeft = popoutMode && isLabelLeft; // Pop out the entire wrapper when label is on the top, because focus ring includes
  // both. But only popout the inner pieces when label is on the left

  var OuterWrapper = popoutModeLabelTop ? 'div' : Fragment;
  var InnerWrapper = popoutModeLabelLeft ? 'div' : Fragment;
  var popoutWrapperProps = {
    ref: setPopperRef,
    style: _assign({}, popperStyle, popoutStyleProp),
    className: clsx((_clsx = {}, _defineProperty(_clsx, classes.controlAreaPopout, managedPopout && isPoppedOut || !managedPopout && focused), _defineProperty(_clsx, classes.popoutElevatedEffect, popoutMode && popperTallerThanPlaceholder), _clsx))
  };
  var setRootRef = useForkRef(ref, popoutModeLabelTop ? contentRef : noop);
  return /*#__PURE__*/React.createElement(FormFieldPopoutContext.Provider, {
    value: {
      popoutMode: popoutMode,
      popoutActions: actions,
      popoutRef: setPopperRef,
      popperState: popperState,
      popperTallerThanPlaceholder: popperTallerThanPlaceholder,
      setIsPoppedOut: setIsPoppedOut,
      setManagedPopout: setManagedPopout,
      setIntendedHeight: function setIntendedHeight(intendedHeight) {
        if (isLabelLeft) {
          actions.updateHeight(intendedHeight);
        } else if (contentRef.current && controlAreaRef.current) {
          // When the label is on the top, we need to include the label height with the
          // component's height for the popout, by working out difference of the current
          // height of the controlArea and the component height
          actions.updateHeight(contentRef.current.getBoundingClientRect().height - controlAreaRef.current.getBoundingClientRect().height + intendedHeight);
        }
      }
    }
  }, popoutModeLabelTop ? /*#__PURE__*/React.createElement("div", {
    ref: forkedPlaceholderRef,
    style: placeholderStyle
  }) : undefined, /*#__PURE__*/React.createElement(OuterWrapper, popoutModeLabelTop ? popoutWrapperProps : {}, /*#__PURE__*/React.createElement("div", _assign({
    className: clsx(classes.controlContainer, (_clsx2 = {}, _defineProperty(_clsx2, classes.labelTop, labelPlacement === 'top'), _defineProperty(_clsx2, classes.focused, applyFocusClass && !isLabelLeft), _defineProperty(_clsx2, classes.lowEmphasisFocus, lowEmphasisFocus && !isLabelLeft), _defineProperty(_clsx2, classes.popoutOffsetFocused, popoutMode && popperTallerThanPlaceholder), _clsx2)),
    ref: setRootRef
  }, rest), !isLabelLeft && /*#__PURE__*/React.createElement("div", {
    className: classes.controlAreaBackground
  }), /*#__PURE__*/React.createElement(FormFieldLabel, {
    LabelComponent: LabelComponent,
    LabelProps: LabelProps,
    StatusIndicatorProps: StatusIndicatorProps,
    classes: classes,
    density: density,
    disabled: disabled,
    hasStatusIndicator: hasStatusIndicator,
    label: label,
    labelId: labelId,
    labelPlacement: labelPlacement,
    required: required
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.controlAreaWrapper,
    ref: controlAreaRef
  }, popoutModeLabelLeft ? /*#__PURE__*/React.createElement("div", {
    ref: forkedPlaceholderRef,
    style: placeholderStyle
  }) : undefined, /*#__PURE__*/React.createElement(InnerWrapper, popoutModeLabelLeft ? popoutWrapperProps : {}, /*#__PURE__*/React.createElement("div", {
    className: clsx(classes.controlArea, (_clsx3 = {}, _defineProperty(_clsx3, classes.focused, applyFocusClass && isLabelLeft), _defineProperty(_clsx3, classes.lowEmphasisFocus, lowEmphasisFocus && isLabelLeft), _defineProperty(_clsx3, classes.popoutOffsetFocused, popoutMode && popperTallerThanPlaceholder), _clsx3)),
    ref: popoutModeLabelLeft ? contentRef : undefined
  }, isLabelLeft && /*#__PURE__*/React.createElement("div", {
    className: classes.controlAreaBackground
  }), children, /*#__PURE__*/React.createElement(ActivationIndicator, {
    classes: classes,
    density: density,
    enabled: focusVisible,
    hasIcon: !hasStatusIndicator,
    validationState: validationState
  }))), isLabelLeft && helperText && /*#__PURE__*/React.createElement(HelperTextComponent, _assign({
    classes: {
      root: classes.helperText
    },
    disabled: disabled,
    id: helperTextId
  }, HelperTextProps), helperText)))), !isLabelLeft && helperText && /*#__PURE__*/React.createElement(HelperTextComponent, _assign({
    classes: {
      root: classes.helperText,
      disabled: classes.disabled
    },
    disabled: disabled,
    id: helperTextId
  }, HelperTextProps), helperText));
});

var getStatusIndicatorProps = function getStatusIndicatorProps(_ref6) {
  var TooltipProps = _ref6.TooltipProps,
      statusIndicatorContent = _ref6.statusIndicatorContent,
      tooltipIsOpen = _ref6.tooltipIsOpen,
      density = _ref6.density,
      helperTextPlacement = _ref6.helperTextPlacement,
      helperText = _ref6.helperText,
      state = _ref6.validationState,
      statusIndicatorId = _ref6.statusIndicatorId,
      classes = _ref6.classes;
  var hasTooltip = !!(helperTextPlacement === 'tooltip' && helperText) || !!(statusIndicatorContent && statusIndicatorContent.length);
  var statusIndicatorTitle = statusIndicatorContent && /*#__PURE__*/React.createElement("div", {
    className: clsx(classes.statusIndicatorContainer, classes["".concat(density, "Density")]),
    id: statusIndicatorId
  }, statusIndicatorContent.map(function (message) {
    return /*#__PURE__*/React.createElement(StatusIndicatorMessage, {
      className: classes.statusIndicatorMessage,
      key: message
    }, message);
  }));
  var title = statusIndicatorTitle || hasTooltip && helperText;
  return {
    hasTooltip: hasTooltip,
    TooltipProps: _assign({
      open: tooltipIsOpen,
      title: title
    }, TooltipProps),
    state: state
  };
};

var getHasStatusIndicator = function getHasStatusIndicator(_ref7) {
  var hasStatusIndicator = _ref7.hasStatusIndicator,
      validationState = _ref7.validationState,
      helperTextPlacement = _ref7.helperTextPlacement,
      helperText = _ref7.helperText;
  return hasStatusIndicator && !!(validationState || helperTextPlacement === 'tooltip' && helperText);
};

var getA11yValue = function getA11yValue(_ref8) {
  var label = _ref8.label,
      ariaLabelledBy = _ref8.ariaLabelledBy,
      ariaDescribedBy = _ref8.ariaDescribedBy,
      helperText = _ref8.helperText,
      helperTextId = _ref8.helperTextId,
      labelId = _ref8.labelId,
      hasStatusIndicator = _ref8.hasStatusIndicator,
      statusIndicatorContent = _ref8.statusIndicatorContent,
      statusIndicatorId = _ref8.statusIndicatorId,
      required = _ref8.required,
      disabled = _ref8.disabled,
      readOnly = _ref8.readOnly;
  var hasStatusIndicatorMessage = hasStatusIndicator && statusIndicatorContent && statusIndicatorContent.length;
  var hasAriaLabelledBy = label || ariaLabelledBy;
  var hasAriaDescribedBy = helperText || ariaDescribedBy || hasStatusIndicatorMessage;
  return {
    'aria-labelledby': hasAriaLabelledBy ? clsx(ariaLabelledBy, labelId) : undefined,
    'aria-describedby': hasAriaDescribedBy ? clsx(ariaDescribedBy, helperText && helperTextId, hasStatusIndicatorMessage && statusIndicatorId) : undefined,
    'aria-required': required,
    disabled: disabled,
    readOnly: readOnly
  };
};

var FormField = /*#__PURE__*/forwardRef(function FormField(_ref9, ref) {
  var _clsx4;

  var ariaLabelledBy = _ref9['aria-labelledby'],
      ariaDescribedBy = _ref9['aria-describedby'],
      children = _ref9.children,
      classes = _ref9.classes,
      className = _ref9.className,
      densityProp = _ref9.density,
      disabled = _ref9.disabled,
      _ref9$fullWidth = _ref9.fullWidth,
      fullWidth = _ref9$fullWidth === void 0 ? true : _ref9$fullWidth,
      _ref9$hasStatusIndica = _ref9.hasStatusIndicator,
      hasStatusIndicator = _ref9$hasStatusIndica === void 0 ? false : _ref9$hasStatusIndica,
      helperText = _ref9.helperText,
      _ref9$HelperTextCompo = _ref9.HelperTextComponent,
      HelperTextComponent = _ref9$HelperTextCompo === void 0 ? FormHelperText : _ref9$HelperTextCompo,
      _ref9$helperTextPlace = _ref9.helperTextPlacement,
      helperTextPlacement = _ref9$helperTextPlace === void 0 ? 'bottom' : _ref9$helperTextPlace,
      HelperTextProps = _ref9.HelperTextProps,
      label = _ref9.label,
      _ref9$LabelComponent = _ref9.LabelComponent,
      LabelComponent = _ref9$LabelComponent === void 0 ? FormLabel : _ref9$LabelComponent,
      _ref9$labelPlacement = _ref9.labelPlacement,
      labelPlacement = _ref9$labelPlacement === void 0 ? 'top' : _ref9$labelPlacement,
      _ref9$LabelProps = _ref9.LabelProps,
      LabelProps = _ref9$LabelProps === void 0 ? {
    displayedNecessity: Necessity.REQUIRED
  } : _ref9$LabelProps,
      lowEmphasisFocus = _ref9.lowEmphasisFocus,
      popoutMode = _ref9.popoutMode,
      _ref9$TooltipComponen = _ref9.TooltipComponent,
      TooltipComponent = _ref9$TooltipComponen === void 0 ? Tooltip : _ref9$TooltipComponen,
      TooltipProps = _ref9.TooltipProps,
      readOnly = _ref9.readOnly,
      _ref9$required = _ref9.required,
      required = _ref9$required === void 0 ? false : _ref9$required,
      statusIndicatorContent = _ref9.statusIndicatorContent,
      statusIndicatorIdOverride = _ref9.statusIndicatorId,
      validationState = _ref9.validationState,
      _ref9$variant = _ref9.variant,
      variant = _ref9$variant === void 0 ? 'theme' : _ref9$variant,
      StatusIndicatorOverrides = _ref9.StatusIndicatorProps,
      rest = _objectWithoutProperties(_ref9, ["aria-labelledby", "aria-describedby", "children", "classes", "className", "density", "disabled", "fullWidth", "hasStatusIndicator", "helperText", "HelperTextComponent", "helperTextPlacement", "HelperTextProps", "label", "LabelComponent", "labelPlacement", "LabelProps", "lowEmphasisFocus", "popoutMode", "TooltipComponent", "TooltipProps", "readOnly", "required", "statusIndicatorContent", "statusIndicatorId", "validationState", "variant", "StatusIndicatorProps"]);

  var density = useDensity(densityProp);
  var labelId = useId(LabelProps && LabelProps.id);
  var helperTextId = useId(HelperTextProps && HelperTextProps.id);
  var statusIndicatorId = useId(statusIndicatorIdOverride);

  var _useIsHoveredOrFocuse = useIsHoveredOrFocused(),
      _useIsHoveredOrFocuse2 = _slicedToArray(_useIsHoveredOrFocuse, 2),
      tooltipIsOpen = _useIsHoveredOrFocuse2[0],
      hoveredOrFocusedMethods = _useIsHoveredOrFocuse2[1];

  var _useState3 = useState(true),
      focusVisible = _useState3[0],
      setFocusVisible = _useState3[1];

  var controlContainerRef = useRef(null);
  var childrenRef = useRef(null);

  var _useState4 = useState(false),
      isFocused = _useState4[0],
      setIsFocused = _useState4[1];

  var _useState5 = useState(false),
      isFieldset = _useState5[0],
      setIsFieldset = _useState5[1];

  var prevHelperTextRef = useRef(null);
  var prevHelperText = prevHelperTextRef.current;

  var _useAriaAnnouncer = useAriaAnnouncer(),
      announce = _useAriaAnnouncer.announce;

  useEffect(function () {
    var _childrenRef$current;

    if (((_childrenRef$current = childrenRef.current) === null || _childrenRef$current === void 0 ? void 0 : _childrenRef$current.firstElementChild.nodeName) === 'FIELDSET') {
      setIsFieldset(true);
    }
  }, []);
  useEffect(function () {
    prevHelperTextRef.current = helperText; // eslint-disable-next-line eqeqeq

    var helperTextHasChanged = helperText != prevHelperText;
    var validationOnBlur = helperTextHasChanged && !isFocused;
    var validationInFieldset = helperTextHasChanged && isFieldset;

    if ((validationOnBlur || validationInFieldset) && helperText) {
      announce(helperText);
    }
  }, [announce, helperText, isFieldset, isFocused, prevHelperText]);
  var combinedHoveredOrFocusedMethods = combineMethodsAndProps(hoveredOrFocusedMethods, rest);

  var StatusIndicatorProps = _assign({}, getStatusIndicatorProps({
    TooltipProps: TooltipProps,
    classes: classes,
    density: density,
    tooltipIsOpen: tooltipIsOpen,
    statusIndicatorContent: statusIndicatorContent,
    validationState: validationState,
    helperTextPlacement: helperTextPlacement,
    helperText: helperText,
    statusIndicatorId: statusIndicatorId
  }), {
    TooltipComponent: TooltipComponent
  }, StatusIndicatorOverrides);

  var formControlClasses = {
    root: clsx(classes.root, classes["".concat(density, "Density")], (_clsx4 = {}, _defineProperty(_clsx4, classes.labelLeft, labelPlacement === 'left'), _defineProperty(_clsx4, classes.error, validationState === 'error'), _defineProperty(_clsx4, classes.warning, validationState === 'warning'), _defineProperty(_clsx4, classes.disabled, disabled), _defineProperty(_clsx4, classes.readOnly, readOnly), _defineProperty(_clsx4, classes.themeVariant, variant === 'theme'), _defineProperty(_clsx4, classes.transparentVariant, variant === 'transparent'), _defineProperty(_clsx4, classes.filledVariant, variant === 'filled'), _defineProperty(_clsx4, classes.hasHelperText, helperText !== undefined), _defineProperty(_clsx4, classes.fullWidth, fullWidth), _clsx4), className)
  };
  return /*#__PURE__*/React.createElement(FormControl, _assign({}, rest, {
    classes: formControlClasses,
    disabled: disabled,
    fullWidth: fullWidth
  }, combinedHoveredOrFocusedMethods, {
    onBlur: function onBlur() {
      return setIsFocused(false);
    },
    onFocus: function onFocus() {
      return setIsFocused(true);
    },
    ref: ref
  }), /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: {
      focusVisible: focusVisible,
      labelPlacement: labelPlacement,
      ref: controlContainerRef,
      setFocusVisible: setFocusVisible,
      variant: variant
    }
  }, /*#__PURE__*/React.createElement(a11yContext.Provider, {
    value: getA11yValue({
      label: label,
      ariaLabelledBy: ariaLabelledBy,
      ariaDescribedBy: ariaDescribedBy,
      helperText: helperText,
      helperTextId: helperTextId,
      labelId: labelId,
      hasStatusIndicator: hasStatusIndicator,
      statusIndicatorId: statusIndicatorId,
      statusIndicatorContent: statusIndicatorContent,
      required: required,
      disabled: disabled,
      readOnly: readOnly
    })
  }, /*#__PURE__*/React.createElement(ControlContainer, {
    HelperTextComponent: HelperTextComponent,
    HelperTextProps: HelperTextProps,
    LabelComponent: LabelComponent,
    LabelProps: LabelProps,
    StatusIndicatorProps: StatusIndicatorProps,
    classes: classes,
    density: density,
    disabled: disabled,
    fullWidth: fullWidth,
    hasStatusIndicator: getHasStatusIndicator({
      hasStatusIndicator: hasStatusIndicator,
      validationState: validationState,
      helperTextPlacement: helperTextPlacement,
      helperText: helperText
    }),
    helperText: helperTextPlacement !== 'tooltip' ? helperText : undefined,
    helperTextId: helperTextId,
    label: label,
    labelId: labelId,
    labelPlacement: labelPlacement,
    lowEmphasisFocus: lowEmphasisFocus,
    popoutMode: popoutMode,
    ref: controlContainerRef,
    required: required,
    validationState: validationState,
    variant: variant
  }, helperTextPlacement === 'tooltip' && !hasStatusIndicator ? /*#__PURE__*/React.createElement(TooltipComponent, _assign({
    id: helperTextId,
    state: validationState,
    title: helperText
  }, TooltipProps), /*#__PURE__*/React.createElement("div", {
    ref: childrenRef
  }, children)) : /*#__PURE__*/React.createElement("div", {
    ref: childrenRef
  }, children)))));
});
process.env.NODE_ENV !== "production" ? ControlContainer.propTypes = {
  HelperTextComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  // eslint-disable-next-line react/forbid-prop-types
  HelperTextProps: PropTypes.object,
  LabelComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  // eslint-disable-next-line react/forbid-prop-types
  LabelProps: PropTypes.object,

  /**
   * The wrapped control
   */
  children: PropTypes.node,

  /**
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * The density of a component affects the style of the layout.
   * A high density component uses minimal sizing and spacing to convey the intended UI design.
   * Conversely, a low density component, maximizes the use of space and size to convey the UI Design.
   */
  density: PropTypes.oneOf(['touch', 'low', 'medium', 'high']),

  /**
   * If `true`, the FormField will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * If, `true`, the FormField will occupy the full width of it's container
   * defaults to `true`
   */
  fullWidth: PropTypes.bool,

  /**
   * The helper text content
   */
  helperText: PropTypes.string,

  /**
   * Id of the form helper text element
   */
  helperTextId: PropTypes.string,

  /**
   * The label value for the FormField
   */
  label: PropTypes.string,

  /**
   * The label id for the FormField
   */
  labelId: PropTypes.string,

  /**
   * Location the label, values: 'top' (default), 'left'
   */
  labelPlacement: PropTypes.oneOf(['top', 'left']),

  /**
   * hides the outer focus ring
   */
  lowEmphasisFocus: PropTypes.bool,

  /**
   * Puts the content of the FormField in popout mode, where occupied space would be kept the same
   * while child element grow in height.
   *
   * @default false
   */
  popoutMode: PropTypes.bool,

  /**
   * Error state string: 'warning', 'error' or '' (default)
   */
  validationState: PropTypes.oneOf(['warning', 'error', '']),

  /**
   * Changes the background color of the FormField
   * values: 'filled, 'theme' (default), 'transparent'
   */
  variant: PropTypes.oneOf(['filled', 'transparent', 'theme'])
} : void 0;
process.env.NODE_ENV !== "production" ? FormFieldLabel.propTypes = {
  LabelComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  // eslint-disable-next-line react/forbid-prop-types
  LabelProps: PropTypes.object,

  /**
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * If `true`, the FormField will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * The label value for the FormField
   */
  label: PropTypes.string,

  /**
   * The label id for the FormField
   */
  labelId: PropTypes.string,

  /**
   * Location the label, values: 'top' (default), 'left'
   */
  labelPlacement: PropTypes.oneOf(['top', 'left'])
} : void 0;
process.env.NODE_ENV !== "production" ? FormField.propTypes = _assign({}, ControlContainer.propTypes, FormFieldLabel.propTypes, {
  /**
   * The component used for the helperText.
   */
  HelperTextComponent: PropTypes.elementType,

  /**
   * Props to be applied to the HelperText used to display info messages
   */
  // eslint-disable-next-line react/forbid-prop-types
  HelperTextProps: PropTypes.object,

  /**
   * The component used for the label.
   */
  LabelComponent: PropTypes.elementType,

  /**
   * Props to be applied to the <label> element
   */
  // eslint-disable-next-line react/forbid-prop-types
  LabelProps: PropTypes.object,

  /**
   * Override props to be used with the StatusIndicator component
   */
  // eslint-disable-next-line react/forbid-prop-types
  StatusIndicatorProps: PropTypes.object,

  /**
   * The component used for the tooltip.
   */
  TooltipComponent: PropTypes.elementType,

  /**
   * Props to be applied to the Tooltip used to display error messages
   */
  // eslint-disable-next-line react/forbid-prop-types
  TooltipProps: PropTypes.object,

  /**
   * aria-describedby to be prepended to the form control aria-labelledby (e.g. when used in a form group with a label)
   */
  'aria-describedby': PropTypes.string,

  /**
   * aria-labelledby to be prepended to the form control aria-labelledby (e.g. when used in a form group with a label)
   */
  'aria-labelledby': PropTypes.string,

  /**
   * The wrapped control
   */
  children: PropTypes.node,

  /**
   * The className(s) of the component
   */
  className: PropTypes.string,

  /**
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * The density of a component affects the style of the layout.
   * A high density component uses minimal sizing and spacing to convey the intended UI design.
   * Conversely, a low density component, maximizes the use of space and size to convey the UI Design.
   */
  density: PropTypes.oneOf(['touch', 'low', 'medium', 'high']),

  /**
   * If `true`, the FormField will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Whether to show the StatusIndicator component for validation states
   */
  hasStatusIndicator: PropTypes.bool,

  /**
   * The helper text content
   */
  helperText: PropTypes.string,

  /**
   * Location the helper text, values: 'bottom' (default) or 'tooltip'
   */
  helperTextPlacement: PropTypes.oneOf(['bottom', 'tooltip']),

  /**
   * The label value for the FormField
   */
  label: PropTypes.string,

  /**
   * Location the label, values: 'top' (default), 'left'
   */
  labelPlacement: PropTypes.oneOf(['top', 'left']),

  /**
   * hides the outer focus ring
   */
  lowEmphasisFocus: PropTypes.bool,

  /**
   * The FormField value is Readonly
   */
  readOnly: PropTypes.bool,

  /**
   * The FormField is required
   */
  required: PropTypes.bool,

  /**
   * List of messages to show in the status indicator Tooltip
   */
  statusIndicatorContent: PropTypes.arrayOf(PropTypes.string),

  /**
   * Override for the generated statusIndicatorId which is used in aria-describedBy
   */
  statusIndicatorId: PropTypes.string,

  /**
   * Error state string: 'warning', 'error' or '' (default)
   */
  validationState: PropTypes.oneOf(['warning', 'error', '']),

  /**
   * Changes the background color of the FormField
   * values: 'filled, 'theme' (default), 'transparent'
   */
  variant: PropTypes.oneOf(['filled', 'transparent', 'theme'])
}) : void 0;
export default withStyles(style, {
  name: 'FormField'
})(FormField);